/**
 * Wave Bid-Hardening · 2026-05-11
 * Cloudflare Turnstile widget reusável.
 *
 * Carrega o script challenges.cloudflare.com/turnstile/v0/api.js
 * (lazy · 1x por sessão) e renderiza o widget invisível. Em modo
 * "managed", Cloudflare decide se mostra checkbox ou passa silencioso.
 *
 * Uso:
 *   const tsRef = useRef<TurnstileHandle>(null);
 *   <TurnstileWidget ref={tsRef} action="quotation_submit" onToken={setToken} />
 *   const token = await tsRef.current?.getToken();
 *
 * Se VITE_TURNSTILE_SITE_KEY não estiver setado (dev), renderiza
 * placeholder e retorna token vazio · backend faz fail-open em dev.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useT } from '@/hooks/useT';

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: string | HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'invisible';
          language?: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      remove: (widgetId?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_script_load_failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  /** Retorna token atual ou aguarda widget renderizar (até 5s). */
  getToken: () => Promise<string>;
  reset: () => void;
}

interface Props {
  action?: string;
  /** Notifica caller quando token é obtido (auto). */
  onToken?: (token: string) => void;
  className?: string;
}

export const TurnstileWidget = forwardRef<TurnstileHandle, Props>(function TurnstileWidget(
  { action, onToken, className },
  ref,
) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string>('');
  // Onda B · TASK 3 · flicker fix · ver useEffect abaixo. Closure-stable
  // refs pra `action` e `onToken` removem essas dependências do effect
  // — antes, qualquer re-render do pai com novo `onToken` (callback
  // recriado a cada render) disparava cleanup + re-render do widget e
  // causava flicker visual de ~150–200 ms.
  const actionRef = useRef(action);
  actionRef.current = action;
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'disabled'>(
    SITE_KEY ? 'loading' : 'disabled',
  );

  useEffect(() => {
    // Onda B · TASK 3 · F6 flicker fix.
    //
    // Root cause: o effect dependia de `[action, onToken]`. Quando o
    // pai (LoginPage) re-renderiza por qualquer motivo (state update
    // do email, locale change via useT, sessionExpired toggle…), uma
    // referência nova de `onToken` (se passada) ou re-render geral em
    // StrictMode double-mount disparava cleanup → remove widget →
    // re-render do widget. Resultado visual: blink de ~150-200 ms.
    //
    // Fix: deps = `[]` (mount-once). `action` e `onToken` lidos via
    // ref pra evoluir sem re-mount. StrictMode double-invoke continua
    // a chamar cleanup uma vez (esperado em dev), mas em prod só monta
    // 1×.
    if (!SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        if (!window.turnstile || !containerRef.current) return;
        // Guard: se já renderizou (StrictMode double-invoke ou hot reload),
        // não renderiza de novo — Turnstile lança se chamar render() 2× no
        // mesmo container sem remove() entre as chamadas.
        if (widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action: actionRef.current,
          theme: 'auto',
          size: 'normal',
          // W158 · 2026-06-02 — força PT-BR no desafio (antes herdava o locale
          // do navegador → "Verify you are human" pra fornecedor/cliente BR).
          language: 'pt-br',
          callback: (t: string) => {
            tokenRef.current = t;
            onTokenRef.current?.(t);
          },
          'error-callback': () => {
            tokenRef.current = '';
            setStatus('error');
          },
          'expired-callback': () => {
            tokenRef.current = '';
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getToken: async () => {
        if (status === 'disabled') return ''; // dev sem site key
        if (status === 'error') return '';    // widget falhou (domínio não allowlisted etc.) — falha rápida
        // Aguarda até 5s pelo token
        const start = Date.now();
        while (!tokenRef.current && Date.now() - start < 5000) {
          await new Promise((r) => setTimeout(r, 100));
        }
        return tokenRef.current;
      },
      reset: () => {
        tokenRef.current = '';
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    [status],
  );

  if (status === 'disabled') {
    return (
      <div className={className}>
        <p className="text-[11px] text-ink-subtle">
          {isEn
            ? 'Human verification disabled in development.'
            : 'Verificação de humano desativada em desenvolvimento.'}
        </p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={className}>
        <p className="text-[11px] text-err">
          {isEn
            ? 'Failed to load security verification. Reload the page.'
            : 'Falha ao carregar verificação de segurança. Recarregue a página.'}
        </p>
      </div>
    );
  }
  return (
    <div className={className}>
      <div ref={containerRef} />
      {status === 'loading' && (
        <p className="text-[11px] text-ink-subtle">
          {isEn ? 'Loading security verification…' : 'Carregando verificação de segurança…'}
        </p>
      )}
    </div>
  );
});
